import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
    IconButton,
    TablePagination,
    Dialog,
    DialogContent,
    Typography,
    Box,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { z } from "zod";
import ListLayout from "../../components/Layout/ListLayout";
import {
    useListCategories,
    useCreateCategory,
    useEditCategory,
    useDeleteCategory,
} from "../../api/bpm/endpoints";
import { useAuth } from "../../auth";
import CategoryForm, { CategoryFormData } from "../../forms/CategoryForm";
import CategoryFilters, { CategoryFilterValues } from "../../components/Categories/CategoryFilters";
import CategoryList from "../../components/Categories/CategoryList";
import { CategoryDto } from "../../api/bpm/model/categoryDto";
import { useTranslation } from "react-i18next";

const categoriesSearchSchema = z.object({
    page: z.number().catch(1),
    size: z.number().catch(10),
    q: z.string().optional(),
    orderBy: z.string().catch('name'),
    order: z.enum(['asc', 'desc']).catch('asc'),
});

export const Route = createFileRoute("/__auth/categories")({
    component: CategoriesListComponent,
    validateSearch: (search) => categoriesSearchSchema.parse(search),
    staticData: {
        title: "routes.categories",
    },
});

function CategoriesListComponent() {
    const { user } = useAuth();
    const tenant = user?.tenant || "default-domain";
    const navigate = Route.useNavigate();
    const { t } = useTranslation();
    const search = Route.useSearch();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<CategoryDto | null>(null);
    const [deletingCategory, setDeletingCategory] = useState<CategoryDto | null>(null);

    const [errorSnackbar, setErrorSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });
    const [successSnackbar, setSuccessSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

    const {
        data: categoriesData,
        isLoading,
        error,
        refetch
    } = useListCategories(tenant);

    const categories = (categoriesData as any) || [];

    const createMutation = useCreateCategory();
    const updateMutation = useEditCategory();
    const deleteMutation = useDeleteCategory();

    const sortedAndFilteredData = useMemo(() => {
        let result = (categories as any) || [];

        if (search.q) {
            const query = search.q.toLowerCase();
            result = result.filter((c: any) => c.name?.toLowerCase().includes(query));
        }

        const { orderBy, order } = search;
        result = [...result].sort((a: any, b: any) => {
            const valueA = a[orderBy] || '';
            const valueB = b[orderBy] || '';

            if (valueA < valueB) return order === 'asc' ? -1 : 1;
            if (valueA > valueB) return order === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [categories, search.q, search.orderBy, search.order]);

    const paginatedData = useMemo(() => {
        const start = (search.page - 1) * search.size;
        const end = start + search.size;
        return sortedAndFilteredData.slice(start, end);
    }, [sortedAndFilteredData, search.page, search.size]);

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({
            search: (prev) => ({ ...prev, page: newPage + 1 }),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        navigate({
            search: (prev) => ({ ...prev, size: parseInt(event.target.value, 10), page: 1 }),
        });
    };

    const handleFilter = (filters: CategoryFilterValues) => {
        navigate({
            search: (prev) => ({ ...prev, ...filters, page: 1 }),
        });
    };

    const handleRequestSort = (property: string) => {
        const isAsc = search.orderBy === property && search.order === 'asc';
        navigate({
            search: (prev) => ({
                ...prev,
                orderBy: property,
                order: isAsc ? 'desc' : 'asc'
            })
        });
    };

    const handleCreateCategory = async (data: CategoryFormData) => {
        try {
            await createMutation.mutateAsync({
                tenant,
                data: {
                    name: data.name
                }
            });
            setIsCreateModalOpen(false);
            refetch();
            setSuccessSnackbar({ open: true, message: t("categories.createdSuccess") });
        } catch (err: any) {
            console.error("Erro ao criar categoria:", err);
            const message = err?.body?.message || err?.message || t("categories.failedToCreate");
            setErrorSnackbar({ open: true, message });
        }
    };

    const handleUpdateCategory = async (data: CategoryFormData) => {
        if (!editingCategory?.id) return;
        try {
            await updateMutation.mutateAsync({
                tenant,
                categoryId: editingCategory.id,
                data: {
                    name: data.name
                }
            });
            setEditingCategory(null);
            refetch();
            setSuccessSnackbar({ open: true, message: t("categories.editedSuccess") });
        } catch (err: any) {
            console.error("Erro ao editar categoria:", err);
            const message = err?.body?.message || err?.message || t("categories.failedToEdit");
            setErrorSnackbar({ open: true, message });
        }
    };

    const handleDeleteCategory = async () => {
        if (!deletingCategory?.id) return;
        try {
            await deleteMutation.mutateAsync({
                tenant,
                categoryId: deletingCategory.id
            });
            setDeletingCategory(null);
            refetch();
            setSuccessSnackbar({ open: true, message: t("categories.deletedSuccess") });
        } catch (err: any) {
            console.error("Erro ao excluir categoria:", err);
            const message = err?.body?.message || err?.message || t("categories.failedToDelete");
            setErrorSnackbar({ open: true, message });
        }
    };

    return (
        <ListLayout
            title={t("categories.title")}
            subtitle={t("categories.subtitle")}
            filters={
                <CategoryFilters
                    onFilter={handleFilter}
                    isLoading={isLoading}
                    initialValues={{ q: search.q }}
                />
            }
            isLoading={isLoading}
            isEmpty={!isLoading && sortedAndFilteredData.length === 0}
            error={error}
            actions={
                <IconButton
                    onClick={() => setIsCreateModalOpen(true)}
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                        boxShadow: 2
                    }}
                >
                    <AddIcon />
                </IconButton>
            }
        >
            <CategoryList
                categories={paginatedData}
                onEdit={setEditingCategory}
                onDelete={setDeletingCategory}
                orderBy={search.orderBy}
                order={search.order}
                onRequestSort={handleRequestSort}
            />

            <TablePagination
                component="div"
                count={sortedAndFilteredData.length}
                page={search.page - 1}
                onPageChange={handlePageChange}
                rowsPerPage={search.size}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage={t("common.rowsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                    mt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            />

            <Dialog
                open={isCreateModalOpen}
                onClose={() => !createMutation.isPending && setIsCreateModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <CategoryForm
                        onSubmit={handleCreateCategory}
                        onCancel={() => setIsCreateModalOpen(false)}
                        isLoading={createMutation.isPending}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!editingCategory}
                onClose={() => !updateMutation.isPending && setEditingCategory(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    {editingCategory && (
                        <CategoryForm
                            initialData={{ name: editingCategory.name || "" }}
                            onSubmit={handleUpdateCategory}
                            onCancel={() => setEditingCategory(null)}
                            isLoading={updateMutation.isPending}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!deletingCategory}
                onClose={() => !deleteMutation.isPending && setDeletingCategory(null)}
                maxWidth="xs"
                fullWidth
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        {t("categories.deleteTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t("categories.deleteConfirmation", { name: deletingCategory?.name })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDeleteCategory}
                            disabled={deleteMutation.isPending}
                            sx={{ fontWeight: 700 }}
                        >
                            {t("common.delete").toUpperCase()}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setDeletingCategory(null)}
                            disabled={deleteMutation.isPending}
                            sx={{ fontWeight: 700 }}
                        >
                            {t("common.cancel").toUpperCase()}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Snackbar
                open={errorSnackbar.open}
                autoHideDuration={5000}
                onClose={() => setErrorSnackbar({ open: false, message: "" })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setErrorSnackbar({ open: false, message: "" })}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {errorSnackbar.message}
                </Alert>
            </Snackbar>
            <Snackbar
                open={successSnackbar.open}
                autoHideDuration={4000}
                onClose={() => setSuccessSnackbar({ open: false, message: "" })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSuccessSnackbar({ open: false, message: "" })}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {successSnackbar.message}
                </Alert>
            </Snackbar>
        </ListLayout>
    );
}
